import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Portal, Flex, Text, IconButton, duration, space, icon as iconSize, iconWeight, Icon, useBreakpoint, useReducedMotion, Input, Skeleton } from '@nula/core-ui';
import { useNavigateToEntity } from '@/hooks/navigation';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { search } from '@/lib/platform';
import { type SearchHit } from '@/lib/search/search-service';
import clsx from 'clsx';
import styles from './quick-find.module.css';

const DEBOUNCE_MS = 150;

interface QuickFindProps {
  open: boolean;
  onClose: () => void;
}

function resultIcon(type?: string) {
  switch (type) {
    case 'collection': return 'layers';
    default: return 'description';
  }
}

export function QuickFind({ open, onClose }: QuickFindProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const shouldReduce = useReducedMotion();
  const bp = useBreakpoint();

  const navigateToEntity = useNavigateToEntity();
  const allDocs = useWorkspaceDocs();
  const allDocuments = useMemo(() => allDocs.filter((d) => !d.isArchived), [allDocs]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), duration.fast);
    } else {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      search.searchDocuments(query)
        .then((docs) => {
          if (!cancelled) {
            setResults(docs);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, open]);

  const handleSelect = useCallback((doc: { id: string }) => {
    navigateToEntity(doc.id);
    onClose();
  }, [navigateToEntity, onClose]);

  // Show recent documents when no query
  const recentDocs = query.trim() ? [] : allDocuments.slice(0, 5);
  const displayResults = query.trim() ? results : [];

  // Reset selection when results change
  useEffect(() => { setSelectedIndex(0); }, [results, query]);

  const allItems = query.trim() ? results : recentDocs;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && allItems.length > 0) {
      const item = allItems[selectedIndex];
      if (item) {
        navigateToEntity(item.id);
        onClose();
      }
    }
  };

  // Breakpoint-driven CSS custom properties — useBreakpoint preserved per migration rules.
  // Split between backdrop and modal so the modal element carries max-width/margin/radius vars
  // (tests walk up from the input looking for style attributes with these properties).
  const backdropVars = {
    '--quickfind-backdrop-padding': bp.md ? `var(--space-4)` : '0',
  } as React.CSSProperties;
  const modalVars = {
    '--quickfind-panel-max-width': bp.md ? `calc(var(--space-8) * 14)` : '100vw',
    '--quickfind-panel-radius': bp.md ? 'var(--radius-xl)' : '0',
    '--quickfind-panel-margin-top': bp.md ? '10vh' : '0',
  } as React.CSSProperties;

  // No-results icon size: space[8] + space[4] = 48+16 = 64px
  const noResultsIconSize = space[8] + space[4];

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={shouldReduce ? { duration: 0 } : { duration: duration.slow / 1000 }}
            onClick={onClose}
            className={styles.backdrop}
            style={backdropVars}
          >
            {/* Modal */}
            <motion.div
              initial={shouldReduce ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: -space[5] }}
              animate={shouldReduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
              exit={shouldReduce ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: -space[5] }}
              transition={shouldReduce ? { duration: 0 } : {
                type: 'spring',
                damping: 25,
                stiffness: 300,
              }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="quickfind-title"
              className={styles.modal}
              style={modalVars}
            >
              <span id="quickfind-title" className={styles.srOnly}>
                Quick Find
              </span>
              {/* Search input */}
              <Flex
                align="center"
                gap={space[3]}
                className={styles.searchRow}
              >
                <Icon name="search" size={space[5]} weight={iconWeight.light} className={styles.searchIcon} />
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Find a document, canvas, or action..."
                  focusEffect={false}
                  className={styles.searchInput}
                />
                <IconButton
                  size="sm"
                  onClick={onClose}
                  className={styles.closeBtn}
                >
                  <Icon name="close" size={iconSize.md} weight={iconWeight.light} />
                </IconButton>
              </Flex>

              {/* Results */}
              <Flex vertical className={styles.resultsList}>
                {/* Section label — hidden while searching */}
                {!(loading && query.trim()) && (
                  <Text className={styles.sectionLabel}>
                    {query.trim() ? 'Search Results' : 'Recent'}
                  </Text>
                )}

                {/* Skeleton rows while searching */}
                {loading && query.trim() && (
                  <Flex vertical gap={space[1]} className={styles.resultsGroup}>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Flex key={i} align="center" gap={space[4]} className={styles.resultItem} data-testid="search-skeleton">
                        <Skeleton.Avatar active size={space[5]} shape="square" />
                        <Flex className={styles.skeletonTitle}>
                          <Skeleton active title={{ width: '100%' }} paragraph={false} />
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                )}

                {/* Search results */}
                {!loading && displayResults.length > 0 && (
                  <Flex vertical role="listbox" aria-label="Search results" className={styles.resultsGroup}>
                    {displayResults.map((doc, i) => {
                      const title = doc.title || 'Untitled';
                      return (
                        <ResultItem
                          key={doc.id}
                          icon={resultIcon(doc.kind)}
                          title={title}
                          selected={selectedIndex === i}
                          onClick={() => handleSelect(doc)}
                          showEnterHint={selectedIndex === i}
                        />
                      );
                    })}
                  </Flex>
                )}

                {/* Recent docs (no query) */}
                {!query.trim() && recentDocs.length > 0 && (
                  <Flex vertical role="listbox" aria-label="Recent documents" className={styles.resultsGroup}>
                    {recentDocs.map((doc, i) => (
                      <ResultItem
                        key={doc.id}
                        icon={resultIcon(doc.kind)}
                        title={doc.title || 'Untitled'}
                        selected={selectedIndex === i}
                        onClick={() => {
                          navigateToEntity(doc.id);
                          onClose();
                        }}
                        showEnterHint={selectedIndex === i}
                      />
                    ))}
                  </Flex>
                )}

                {/* Quick actions (no query, no recent) */}
                {!query.trim() && recentDocs.length === 0 && (
                  <Flex vertical role="listbox" aria-label="Quick actions" className={styles.resultsGroup}>
                    <ResultItem icon="dashboard" title="Go to Dashboard" onClick={onClose} />
                    <ResultItem icon="description" title="New Reflection" onClick={onClose} />
                  </Flex>
                )}

                {/* No results */}
                {query.trim() && !loading && displayResults.length === 0 && (
                  <Flex vertical align="center" justify="center" className={styles.noResultsPane}>
                    <Flex
                      align="center"
                      justify="center"
                      className={styles.noResultsIconWrap}
                      style={{ '--quickfind-no-results-icon-size': `${noResultsIconSize}px` } as React.CSSProperties}
                    >
                      <Icon name="search" size={space[5]} weight={iconWeight.light} />
                    </Flex>
                    <Text className={styles.noResultsText}>
                      No whispers found for &ldquo;{query}&rdquo;
                    </Text>
                  </Flex>
                )}
              </Flex>

              {/* Footer */}
              <Flex
                align="center"
                justify="space-between"
                className={styles.footer}
              >
                <Flex align="center" gap={space[2]}>
                  <Text className={styles.kbdKey}>↑↓</Text>
                  <Text size="xs" type="secondary">to navigate</Text>
                </Flex>
                <Flex align="center" gap={space[2]}>
                  <Text className={styles.kbdKey}>esc</Text>
                  <Text size="xs" type="secondary">to close</Text>
                </Flex>
              </Flex>
            </motion.div>
          </motion.div>
        </Portal>
      )}
    </AnimatePresence>
  );
}

// ── Result item ──────────────────────────────────────────────────────────────

function ResultItem({ icon, title, onClick, showEnterHint = false, selected = false }: { icon: string; title: string; onClick: () => void; showEnterHint?: boolean; selected?: boolean }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const highlighted = hovered || selected;

  // Scroll into view when selected via keyboard
  useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  return (
    <Flex
      ref={ref}
      align="center"
      gap={space[4]}
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={clsx(styles.resultItem, highlighted && styles.resultItemHighlighted)}
    >
      <Flex
        align="center"
        justify="center"
        className={clsx(styles.resultIconWrap, highlighted && styles.resultIconWrapHighlighted)}
      >
        <Icon name={icon} size={iconSize.md} weight={iconWeight.light} />
      </Flex>
      <Text className={clsx(styles.resultTitle, highlighted && styles.resultTitleHighlighted)}>
        {title}
      </Text>
      {showEnterHint && (
        <Text className={styles.enterHint}>
          Press Enter
        </Text>
      )}
    </Flex>
  );
}
